import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Plate } from "@/components/StatusBadge";
import { label, formatRupiah } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Search, PackageCheck, Truck, Warehouse, FolderOpen } from "lucide-react";
import { useUrlState } from "@/lib/navState";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export function ReceiveW({ row, onDone }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [supplier, setSupplier] = useState(row.supplier || "");
  const [dn, setDn] = useState("");
  const remaining = Math.max(0, row.quantity - row.received_qty);
  const submit = async () => {
    const n = Number(qty);
    if (!n || n <= 0) { toast.error("Enter received quantity"); return; }
    if (n > remaining) { toast.error(`Only ${remaining} remaining to receive`); return; }
    try {
      await api.post(`/claims/${row.claim_id}/receive-part`, { part_id: row.part_id, received_now: n, supplier, delivery_note: dn });
      toast.success("Received · Warehouse RESERVED FOR CLAIM");
      setOpen(false); setQty(""); setDn(""); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`wh-receive-open-${row.part_id}`} size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><PackageCheck size={14} className="mr-1" /> Receive</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Receive Part</DialogTitle><DialogDescription>Additive receipt — inherited from Claim {row.license_plate}. Fully received parts auto-reserve to the warehouse.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 rounded-md bg-gray-50 p-3">
            <RO l="License Plate" v={row.license_plate} /><RO l="Part" v={row.part_name} />
            <RO l="Required" v={row.quantity} /><RO l="Received" v={row.received_qty} /><RO l="Remaining" v={remaining} />
            <RO l="Procurement" v={label(row.procurement_source)} />
          </div>
          <div><Label className="text-xs">Received Now *</Label><Input data-testid={`wh-receive-qty-${row.part_id}`} type="number" max={remaining} value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-xs">Supplier / Dealer</Label><Input data-testid={`wh-receive-supplier-${row.part_id}`} value={supplier} onChange={(e) => setSupplier(e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-xs">Delivery Note</Label><Input data-testid={`wh-receive-dn-${row.part_id}`} value={dn} onChange={(e) => setDn(e.target.value)} className="mt-1.5" /></div>
          <div className="flex justify-end"><Button data-testid={`wh-receive-confirm-${row.part_id}`} onClick={submit} className="bg-emerald-600 text-white hover:bg-emerald-700">Confirm Receive</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function IssueW({ row, onDone }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [rcv, setRcv] = useState("");
  const submit = async () => {
    const n = Number(qty);
    if (!n || n <= 0) { toast.error("Enter issue quantity"); return; }
    if (n > row.reserved_qty) { toast.error(`Only ${row.reserved_qty} reserved`); return; }
    try {
      await api.post(`/claims/${row.claim_id}/issue-part`, { part_id: row.part_id, qty: n, received_by: rcv });
      toast.success("Issued to workshop · expected salvage created");
      setOpen(false); setQty(""); setRcv(""); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`wh-issue-open-${row.part_id}`} size="sm" className="bg-[#111827] text-white hover:bg-[#1F2937]"><Truck size={14} className="mr-1" /> Issue</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Issue to Workshop</DialogTitle><DialogDescription>Reduces reserved stock; keeps the audit trail linked to Claim {row.license_plate}. Creates an EXPECTED SALVAGE for the replaced old part.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 rounded-md bg-gray-50 p-3">
            <RO l="License Plate" v={row.license_plate} /><RO l="Part" v={row.part_name} />
            <RO l="Reserved" v={row.reserved_qty} /><RO l="Issued so far" v={row.issued_qty} />
          </div>
          <div><Label className="text-xs">Qty to Issue *</Label><Input data-testid={`wh-issue-qty-${row.part_id}`} type="number" max={row.reserved_qty} value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-xs">Received By (technician)</Label><Input data-testid={`wh-issue-rcv-${row.part_id}`} value={rcv} onChange={(e) => setRcv(e.target.value)} className="mt-1.5" /></div>
          <div className="flex justify-end"><Button data-testid={`wh-issue-confirm-${row.part_id}`} onClick={submit} className="bg-[#111827] text-white hover:bg-[#1F2937]">Confirm Issue</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RO({ l, v }) { return (<div><div className="text-[10px] uppercase tracking-wide text-gray-400">{l}</div><div className="font-medium text-gray-800">{v ?? "-"}</div></div>); }

export function InstallW({ row, onDone }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [tech, setTech] = useState("");
  const outstanding = (row.issued_qty || 0) - (row.installed_qty || 0);
  const submit = async () => {
    const n = Number(qty);
    if (!n || n <= 0) { toast.error("Enter install quantity"); return; }
    if (n > outstanding) { toast.error(`Only ${outstanding} issued & not yet installed`); return; }
    try {
      await api.post(`/claims/${row.claim_id}/install-part`, { part_id: row.part_id, qty: n, technician: tech });
      toast.success("Part terpasang (INSTALLED) · stok tidak dikurangi lagi");
      setOpen(false); setQty(""); setTech(""); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`wh-install-open-${row.part_id}`} size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">Installed</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Confirm Installed</DialogTitle><DialogDescription>Menandai part terpasang di kendaraan {row.license_plate}. Tidak mengurangi stok lagi (stok berkurang saat Part Out). Installed ≤ Issued.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 rounded-md bg-gray-50 p-3">
            <RO l="Part" v={row.part_name} /><RO l="Issued" v={row.issued_qty} />
            <RO l="Installed so far" v={row.installed_qty || 0} /><RO l="Belum terpasang" v={outstanding} />
          </div>
          <div><Label className="text-xs">Qty to Install *</Label><Input data-testid={`wh-install-qty-${row.part_id}`} type="number" max={outstanding} value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-xs">Teknisi / Installer</Label><Input data-testid={`wh-install-tech-${row.part_id}`} value={tech} onChange={(e) => setTech(e.target.value)} className="mt-1.5" /></div>
          <div className="flex justify-end"><Button data-testid={`wh-install-confirm-${row.part_id}`} onClick={submit} className="bg-emerald-600 text-white hover:bg-emerald-700">Confirm Installed</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const ALERT = {
  RED: { cls: "border-l-red-500", dot: "bg-red-500", label: "Parts belum datang · menghambat produksi" },
  YELLOW: { cls: "border-l-amber-400", dot: "bg-amber-400", label: "Diterima sebagian" },
  GREEN: { cls: "border-l-emerald-500", dot: "bg-emerald-500", label: "Parts lengkap tersedia" },
  BLUE: { cls: "border-l-blue-500", dot: "bg-blue-500", label: "Sudah diserahkan / dipasang" },
  GRAY: { cls: "border-l-gray-300", dot: "bg-gray-400", label: "Menunggu pemesanan" },
};

function Stat({ l, v, tone = "text-gray-800" }) {
  return <div className="rounded-md bg-gray-50 px-2 py-1 text-center"><div className={`text-sm font-bold ${tone}`}>{v}</div><div className="text-[9px] uppercase tracking-wide text-gray-400">{l}</div></div>;
}

function FolderCard({ f, onOpen }) {
  const a = ALERT[f.alert] || ALERT.GRAY;
  const c = f.counts;
  return (
    <button data-testid={`wh-folder-${f.plate_key}`} onClick={onOpen} className={`block w-full min-w-0 max-w-full overflow-hidden rounded-lg border border-l-4 border-gray-100 bg-white p-3 text-left shadow-sm transition-shadow hover:shadow-md ${a.cls}`}>
      <div className="flex items-center gap-2">
        <FolderOpen size={18} className="shrink-0 text-[#3B82F6]" />
        <Plate value={f.license_plate} />
        <span className="min-w-0 flex-1 truncate text-xs text-gray-500">{[f.vehicle_brand, f.vehicle_model].filter(Boolean).join(" ")}</span>
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${a.dot}`} title={a.label} />
      </div>
      <div className="mt-1 truncate text-[11px] text-gray-500">{f.customer_name || "-"} · {f.insurance_company || "Non-asuransi"} · SPK aktif: {f.active_spk.join(", ") || "-"} ({f.spk_count} SPK · {f.item_count} item)</div>
      <div className="mt-2 grid grid-cols-4 gap-1 sm:grid-cols-7">
        <Stat l="Butuh" v={c.required} /><Stat l="Dipesan" v={c.ordered} /><Stat l="Diterima" v={c.received} tone="text-emerald-700" />
        <Stat l="Belum" v={c.not_received} tone="text-red-600" /><Stat l="Ke Produksi" v={c.issued} tone="text-violet-700" />
        <Stat l="Terpasang" v={c.installed} tone="text-blue-700" /><Stat l="Outstanding" v={c.outstanding} tone="text-amber-700" />
      </div>
      <div className="mt-1.5 text-[10px] font-semibold text-gray-500">{f.parts_complete ? "PARTS COMPLETE" : a.label}</div>
    </button>
  );
}

function ConsistencyCheck() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setR((await api.get("/warehouse/consistency-check")).data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3" data-testid="wh-consistency">
      <Button data-testid="wh-consistency-run" size="sm" variant="outline" disabled={busy} onClick={run}>{busy ? "Memeriksa…" : "Cek Konsistensi Data (read-only)"}</Button>
      {r && (
        <div data-testid="wh-consistency-result" className="mt-2 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700">
          <div>{r.claims_with_parts} SPK · {r.parts} part · {r.folders} folder nopol ({r.multi_spk_folders} nopol dengan &gt;1 SPK)</div>
          <div className={r.issue_count ? "text-red-600" : "text-emerald-700"}>Selisih qty vs transaksi: {r.issue_count} · Transaksi duplikat: {r.duplicate_count} · Part data lama tanpa transaksi: {r.legacy_parts_without_txn}</div>
          {r.issues.slice(0, 20).map((i, k) => <div key={k} className="text-red-600">• {i.license_plate} {i.spk_number} {i.part_name}: {i.issue}</div>)}
        </div>
      )}
    </div>
  );
}

export default function WarehouseParts() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useUrlState("q", "");
  const [alert, setAlert] = useUrlState("alert", "ALL");
  const navigate = useNavigate();
  const load = useCallback(async () => { try { const { data } = await api.get("/warehouse/vehicles", { params: { search: q } }); setRows(data || []); } catch (e) { setRows([]); } }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const shown = useMemo(() => (rows || []).filter((f) => alert === "ALL" || f.alert === alert), [rows, alert]);
  return (
    <div className="ti-dark -m-3 -mb-24 min-h-[calc(100vh-3rem)] px-4 py-4 sm:-m-5 sm:px-6 lg:-mb-5 lg:px-8 lg:py-6">
      <h1 className="font-heading text-2xl font-bold text-[#111827] sm:text-3xl"><Warehouse size={26} className="mr-2 inline text-[#3B82F6]" />Part Warehouse</h1>
      <p className="mt-1 text-sm text-gray-500">Folder per nomor polisi · rincian sparepart tetap terpisah per SPK</p>
      <ConsistencyCheck />
      <div className="relative mt-5">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input data-testid="wh-parts-search" placeholder="Cari nopol, SPK, customer, part number, nama part, supplier, asuransi…" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-10" />
      </div>
      <div className="-mx-1 mt-3 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {["ALL", "RED", "YELLOW", "GREEN", "BLUE", "GRAY"].map((k) => (
          <button key={k} data-testid={`wh-alert-${k}`} onClick={() => setAlert(k)} className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${alert === k ? "border-[#111827] bg-[#111827] text-white" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>
            {k !== "ALL" && <span className={`h-2 w-2 rounded-full ${ALERT[k].dot}`} />}{k === "ALL" ? "Semua" : ALERT[k].label}
          </button>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 lg:grid-cols-2">
        {shown.map((f) => <FolderCard key={f.plate_key} f={f} onOpen={() => navigate(`/warehouse/parts/${encodeURIComponent(f.plate_key)}`)} />)}
      </div>
      {rows === null && <p className="py-10 text-center text-sm text-gray-400">Memuat…</p>}
      {rows !== null && shown.length === 0 && <p data-testid="wh-folders-empty" className="py-10 text-center text-sm text-gray-400">Tidak ada kendaraan untuk tampilan ini.</p>}
    </div>
  );
}
